import { useEffect, useState } from 'react';
import formatCountdown from 'src/utils/dates/formatCountdown';

type UseTimerProps = {
  initialTime?: number;
  interval?: number;
  enabled?: boolean;
  countUp?: boolean;
  onFinish?: () => void;
};

const TIMER_DEFAULT_TIME = 30000;
const TIMER_DEFAULT_INTERVAL = 1000;

export default function useTimer({
  initialTime = TIMER_DEFAULT_TIME,
  interval = TIMER_DEFAULT_INTERVAL,
  enabled = false,
  countUp,
  onFinish,
}: UseTimerProps) {
  const [time, setTime] = useState<number>();

  useEffect(() => {
    if (enabled) {
      setTime(countUp ? 0 : initialTime);
    }
  }, [enabled, initialTime, countUp]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (!countUp && time && time > 0) {
      timer = setTimeout(() => setTime(time - interval), interval);
    } else if (countUp && time !== undefined && time < initialTime) {
      timer = setTimeout(() => setTime(time + interval), interval);
    }
    return () => clearTimeout(timer);
  }, [time, interval, countUp, initialTime]);

  useEffect(() => {
    if (!countUp) {
      time === 0 && onFinish && onFinish();
    } else {
      time === initialTime && onFinish && onFinish();
    }
  }, [time, countUp, onFinish, initialTime]);

  const resetTimer = () => {
    setTime(countUp ? 0 : initialTime);
  };

  const updateTimer = (ms: number) => {
    setTime((prev) => (prev ? prev + ms : ms));
  };

  const formattedTimer = formatCountdown(time);

  return { time, setTime, resetTimer, formattedTimer, updateTimer };
}
